import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { toast } from "react-toastify";
import "./CopyTradingAccounts.css";

const API_URL = "https://server.account.majestyfx.com/admin_copyaccounts.php";

const CopyTradingAccounts = () => {
  const [copyAccounts, setCopyAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buttonLoading, setButtonLoading] = useState({});

  useEffect(() => {
    fetchCopyAccounts();
  }, []);

  const fetchCopyAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === "success") {
        setCopyAccounts(data.data || []);
        toast.success(`Loaded ${data.count} copy trading accounts`);
      } else {
        throw new Error(data.message || "Failed to fetch copy trading accounts");
      }
    } catch (error) {
      console.error("Error fetching copy accounts:", error);
      setError(error.message);
      toast.error("Failed to load copy trading accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (accountId, newStatus) => {
    const loadingKey = `${accountId}_status_${newStatus}`;
    
    try {
      setButtonLoading(prev => ({ ...prev, [loadingKey]: true }));
      
      // Simulate API call for status update
      // Replace this with actual API endpoint when available
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update local state
      setCopyAccounts(prev => 
        prev.map(account => 
          account.id === accountId 
            ? { ...account, status: newStatus }
            : account
        )
      );
      
      toast.success(`Account status updated successfully`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update account status");
    } finally {
      setButtonLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: "Inactive", class: "status-inactive" },
      1: { label: "Active", class: "status-active" },
      2: { label: "Pending", class: "status-pending" },
    };
    
    const statusInfo = statusMap[status] || { label: "Unknown", class: "status-unknown" };
    
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="copy-trading-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading copy trading accounts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="copy-trading-container">
        <div className="error-container">
          <h2>Error Loading Copy Trading Accounts</h2>
          <p>{error}</p>
          <Button onClick={fetchCopyAccounts} className="retry-btn">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="copy-trading-container">
      <div className="page-header">
        <h1>Copy Trading Accounts</h1>
        <div className="header-actions">
          <Button onClick={fetchCopyAccounts} className="refresh-btn">
            <span className="refresh-icon">🔄</span>
            Refresh
          </Button>
        </div>
      </div>

      <div className="accounts-stats">
        <div className="stat-card">
          <h3>Total Accounts</h3>
          <p className="stat-number">{copyAccounts.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active</h3>
          <p className="stat-number active">
            {copyAccounts.filter(acc => acc.status === 1).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p className="stat-number pending">
            {copyAccounts.filter(acc => acc.status === 2).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Inactive</h3>
          <p className="stat-number inactive">
            {copyAccounts.filter(acc => acc.status === 0).length}
          </p>
        </div>
      </div>

      {copyAccounts.length === 0 ? (
        <div className="no-accounts">
          <h3>No Copy Trading Accounts Found</h3>
          <p>There are currently no copy trading accounts in the system.</p>
        </div>
      ) : (
        <div className="accounts-table-container">
          <table className="accounts-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Master Account</th>
                <th>Slave Account</th>
                <th>Copy ID</th>
                <th>Risk Type</th>
                <th>Risk Setting</th>
                <th>Slave Balance</th>
                <th>Slave Equity</th>
                <th>Open Trades</th>
                <th>Floating P/L</th>
                <th>Closed P/L</th>
                <th>Status</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {copyAccounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.id}</td>
                  <td className="email-cell">{account.email}</td>
                  <td>
                    <div className="account-info">
                      <div className="account-name">{account.mastername}</div>
                      <div className="account-id">ID: {account.mastertrade}</div>
                    </div>
                  </td>
                  <td>
                    <div className="account-info">
                      <div className="account-name">{account.slavename}</div>
                      <div className="account-id">ID: {account.slavetrade}</div>
                    </div>
                  </td>
                  <td className="copy-id">{account.copyid}</td>
                  <td>
                    <span className={`risk-type-badge ${account.volumeselect === 'fixed' ? 'risk-fixed' : 'risk-multiplier'}`}>
                      {account.volumeselect === 'fixed' ? 'Fixed' : 'Multiplier'}
                    </span>
                  </td>
                  <td className="risk-setting">{parseFloat(account.volume).toFixed(4)}</td>
                  <td className="balance">${parseFloat(account.slave_balance || 0).toFixed(2)}</td>
                  <td className="equity">${parseFloat(account.slave_equity || 0).toFixed(2)}</td>
                  <td className="open-trades">{account.opentrades || 0}</td>
                  <td className={`floating-pl ${parseFloat(account.slave_floating_pl || 0) >= 0 ? 'positive' : 'negative'}`}>
                    ${parseFloat(account.slave_floating_pl || 0).toFixed(2)}
                  </td>
                  <td className={`closed-pl ${parseFloat(account.slave_closed_pl || 0) >= 0 ? 'positive' : 'negative'}`}>
                    ${parseFloat(account.slave_closed_pl || 0).toFixed(2)}
                  </td>
                  <td>{getStatusBadge(account.status)}</td>
                  <td className="date-cell">{formatDate(account.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CopyTradingAccounts;
